import React, { useState } from "react";
import { api } from "@/lib/api";
import { CATEGORIES, COUNTIES } from "@/lib/constants";
import { toast } from "sonner";
import { Send, Lock, Globe, Shield, ImagePlus, X } from "lucide-react";

export default function SpunePovestea() {
  const [form, setForm] = useState({
    title: "", body: "", category: "alienare", county: "", relation: "tata",
    visibility: "anonymous", contact_ok: false, media_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Doar imagini JPG, PNG sau WEBP");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Fișierul depășește 8 MB");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: resp } = await api.post("/uploads/media", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("media_url", resp.url);
      setMediaPreview(resp.url);
      toast.success("Imagine adăugată (cu watermark TatiSiSofia.ro)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la upload");
    } finally {
      setUploading(false);
    }
  };

  const clearMedia = () => {
    update("media_url", "");
    setMediaPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 8) { toast.error("Titlul este prea scurt"); return; }
    if (form.body.trim().length < 50) { toast.error("Povestea ta este prea scurtă (min. 50 caractere)"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/stories", form);
      setDone(data);
      toast.success("Mulțumim. Povestea ta a fost trimisă.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la trimitere");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 md:py-32 text-center">
        <Shield className="w-14 h-14 text-[#D4A63D] mx-auto mb-6" />
        <h1 className="font-display text-4xl md:text-5xl text-[#F8F8F4] mb-4">Mulțumim.</h1>
        <p className="text-[#A0AABF] text-lg leading-relaxed">{done.message}</p>
        <p className="mt-4 text-sm text-[#6C7A9C]">Cod sesiune: <span className="text-[#D4A63D] font-mono">{done.story_id}</span></p>
        <a href="/" className="btn-gold rounded-sm px-6 py-3 inline-block mt-10">Înapoi la pagina principală</a>
      </div>
    );
  }

  return (
    <div data-testid="page-spune-povestea" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="mb-10 text-center">
        <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-4">Povestea ta contează</p>
        <h1 className="font-display text-4xl md:text-6xl text-[#F8F8F4]">Spune povestea ta</h1>
        <p className="mt-5 text-[#A0AABF] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          Nu ești singur. Experiența ta poate ajuta alți părinți și copii. Poți publica anonim, public sau privat doar pentru echipa noastră.
        </p>
      </header>

      <form onSubmit={submit} className="cinematic-card rounded-lg p-6 md:p-10 space-y-6">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-2 block">Titlu poveste *</label>
          <input
            data-testid="story-title"
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            placeholder="Ex: Nu mi-am văzut copilul timp de 3 ani"
            className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-[#F8F8F4] focus:border-[#D4A63D] outline-none"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-2 block">Categorie</label>
            <select
              data-testid="story-category"
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-[#F8F8F4] focus:border-[#D4A63D] outline-none"
            >
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-2 block">Rol</label>
            <select
              data-testid="story-relation"
              value={form.relation}
              onChange={(e) => update("relation", e.target.value)}
              className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-[#F8F8F4] focus:border-[#D4A63D] outline-none"
            >
              <option value="tata">Tată</option>
              <option value="mama">Mamă</option>
              <option value="bunic">Bunic / Bunică</option>
              <option value="alt">Alt membru al familiei</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-2 block">Județ (opțional)</label>
          <select
            data-testid="story-county"
            value={form.county}
            onChange={(e) => update("county", e.target.value)}
            className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-[#F8F8F4] focus:border-[#D4A63D] outline-none"
          >
            <option value="">— Alege —</option>
            {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-2 block">Povestea ta *</label>
          <textarea
            data-testid="story-body"
            value={form.body}
            onChange={(e) => update("body", e.target.value)}
            rows={10}
            placeholder="Spune ce s-a întâmplat, ce simți, ce ai învățat. Poți edita ulterior."
            className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-[#F8F8F4] focus:border-[#D4A63D] outline-none resize-y"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-2 block">Imagine sau link media (opțional)</label>
          {mediaPreview ? (
            <div className="relative rounded-sm overflow-hidden border border-white/10 bg-[#0A1020]">
              <img src={mediaPreview} alt="Preview" className="w-full max-h-72 object-contain" />
              <button
                type="button"
                onClick={clearMedia}
                data-testid="story-clear-media"
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 grid place-items-center text-white"
                aria-label="Elimină imaginea"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#A0AABF] border-t border-white/5 bg-[#0A1020]/70 flex items-center gap-2">
                <Shield className="w-3 h-3 text-[#D4A63D]" /> Watermark TatiSiSofia.ro aplicat automat
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <label className={`cinematic-card rounded-sm p-4 cursor-pointer flex flex-col items-center justify-center text-center gap-2 hover:border-[#D4A63D]/40 transition-colors ${uploading ? "opacity-50" : ""}`}>
                <ImagePlus className="w-6 h-6 text-[#D4A63D]" />
                <div className="text-[12px] text-[#F8F8F4] font-medium">
                  {uploading ? "Se încarcă..." : "Încarcă o imagine"}
                </div>
                <div className="text-[10px] text-[#6C7A9C]">JPG / PNG / WEBP · max 8 MB · watermark adăugat</div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  onChange={onPickFile}
                  disabled={uploading}
                  data-testid="story-media-upload"
                />
              </label>
              <input
                data-testid="story-media-url"
                value={form.media_url}
                onChange={(e) => { update("media_url", e.target.value); setMediaPreview(e.target.value || null); }}
                placeholder="… sau lipește un link YouTube/imagine"
                className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-[#F8F8F4] focus:border-[#D4A63D] outline-none text-sm"
              />
            </div>
          )}
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-3 block">Mod publicare</label>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { v: "anonymous", icon: Shield, label: "Anonim", hint: "Identitatea ta este ascunsă" },
              { v: "public", icon: Globe, label: "Public", hint: "Cu numele tău (din profil)" },
              { v: "team", icon: Lock, label: "Doar pentru echipă", hint: "Nu va fi publicat" },
            ].map(o => {
              const Icon = o.icon;
              return (
                <button
                  type="button"
                  key={o.v}
                  data-testid={`vis-${o.v}`}
                  onClick={() => update("visibility", o.v)}
                  className={`text-left p-4 rounded-sm border transition-all ${form.visibility === o.v ? "border-[#D4A63D] bg-[#D4A63D]/10" : "border-white/10 hover:border-white/20 bg-[#0A1020]"}`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${form.visibility === o.v ? "text-[#D4A63D]" : "text-[#A0AABF]"}`} />
                  <div className="font-medium text-[#F8F8F4]">{o.label}</div>
                  <div className="text-xs text-[#A0AABF] mt-1">{o.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm text-[#A0AABF]">
          <input
            type="checkbox"
            data-testid="story-contact-ok"
            checked={form.contact_ok}
            onChange={(e) => update("contact_ok", e.target.checked)}
            className="mt-1 accent-[#D4A63D]"
          />
          <span>Sunt de acord ca echipa Tati & Sofia să mă poată contacta confidențial în legătură cu povestea mea.</span>
        </div>

        <p className="text-xs text-[#6C7A9C] leading-relaxed border-t border-white/5 pt-4">
          Confirm că informațiile trimise îmi aparțin și înțeleg că pot fi editate pentru protecția identității și siguranța juridică. Povestea va fi analizată în 24-48h.
        </p>

        <button
          data-testid="story-submit"
          disabled={submitting}
          type="submit"
          className="btn-gold rounded-sm px-7 py-4 w-full text-sm uppercase tracking-widest font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting ? "Se trimite..." : <>Trimite povestea <Send className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}
